import React, { useState, useCallback, useRef, useEffect } from "react";
import { IconShare, IconBrandWhatsapp, IconBrandFacebook, IconBrandX, IconBrandLinkedin, IconLink, IconCheck } from "@tabler/icons-react";

interface ShareButtonProps {
  /** Texto que acompaña la URL al compartir */
  shareText?: string;
  /** utm_source para tracking */
  utmSource?: string;
  /** utm_campaign para tracking */
  utmCampaign?: string;
  /** Clases del botón trigger */
  className?: string;
  /** Dirección del popover: "down" (default), "up", o "up-md-down" (up en mobile, down en md+) */
  popoverPosition?: "down" | "up" | "up-md-down";
  /** Contenido del botón (por defecto "Compartir" + icono) */
  children?: React.ReactNode;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  shareText = "Mira esto en Propi",
  utmSource = "propi",
  utmCampaign = "share",
  popoverPosition = "down",
  className = "flex items-center gap-1 text-white text-xs md:text-sm bg-black/30 hover:bg-black/50 active:bg-black/60 backdrop-blur-sm rounded-xl px-3 py-1.5 transition-colors",
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Cierra el popover al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const getUrlForMedium = useCallback((medium: string) => {
    if (!shareUrl) return "";
    const url = new URL(shareUrl);
    url.searchParams.set("utm_medium", medium);
    return url.href;
  }, [shareUrl]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getUrlForMedium("copy_link"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Error copying link:", err);
    }
  }, [getUrlForMedium]);

  const handleToggle = () => {
    if (!open) {
      const url = new URL(window.location.href);
      url.searchParams.set("utm_source", utmSource);
      url.searchParams.set("utm_campaign", utmCampaign);
      setShareUrl(url.href);
    }
    setOpen(!open);
  };

  const encoded = shareText.replace(/ /g, "%20");
  const whatsapp = `https://api.whatsapp.com/send?text=${encoded}%0A${encodeURIComponent(getUrlForMedium("whatsapp"))}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getUrlForMedium("facebook"))}&quote=${encoded}`;
  const x = `https://twitter.com/intent/tweet?text=${encoded}&url=${encodeURIComponent(getUrlForMedium("x"))}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getUrlForMedium("linkedin"))}`;

  const linkClass = "size-8 flex items-center justify-center rounded-full transition-colors";

  return (
    <div className="relative" ref={popoverRef}>
      <button onClick={handleToggle} className={className}>
        {children ?? (<><span className="hidden md:inline">Compartir</span> <IconShare size={16} /></>)}
      </button>

      {open && (
        <div className={`absolute right-0 bg-white rounded-xl shadow-lg border border-gray-200 p-2 flex items-center gap-1 z-[9999] ${popoverPosition === "up" ? "bottom-full mb-2" : popoverPosition === "up-md-down" ? "bottom-full mb-2 md:bottom-auto md:mb-0 md:top-full md:mt-2" : "top-full mt-2"}`}>
          <a href={whatsapp} target="_blank" rel="noopener noreferrer" className={`${linkClass} text-gray-400 hover:text-[#25D366] hover:bg-[#25D366]/10 active:bg-[#25D366]/20`} title="WhatsApp">
            <IconBrandWhatsapp size={18} />
          </a>
          <a href={facebook} target="_blank" rel="noopener noreferrer" className={`${linkClass} text-gray-400 hover:text-[#1877F2] hover:bg-[#1877F2]/10 active:bg-[#1877F2]/20`} title="Facebook">
            <IconBrandFacebook size={18} />
          </a>
          <a href={x} target="_blank" rel="noopener noreferrer" className={`${linkClass} text-gray-400 hover:text-black hover:bg-black/10 active:bg-black/20`} title="X">
            <IconBrandX size={18} />
          </a>
          <a href={linkedin} target="_blank" rel="noopener noreferrer" className={`${linkClass} text-gray-400 hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 active:bg-[#0A66C2]/20`} title="LinkedIn">
            <IconBrandLinkedin size={18} />
          </a>
          <button onClick={handleCopy} className={`${linkClass} ${copied ? "text-green-600 bg-green-50" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200"}`} title="Copiar enlace">
            {copied ? <IconCheck size={18} /> : <IconLink size={18} />}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
